<!-- TABLE MODE (uses the real table-container / table-header styling so it matches loaded tables) -->
<div *ngIf="type === 'table'" class="table-container">
  <table class="w-full text-left border-collapse text-sm">
    <thead class="border-b border-gray-200">
      <tr>
        <th *ngFor="let colWidth of tableColumns" class="table-header">
          <div [class]="'h-[10px] rounded skeleton-shimmer ' + colWidth"></div>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let _ of range" class="group">
        <td *ngFor="let colWidth of tableColumns">
          <div [class]="'h-[13px] rounded skeleton-shimmer ' + colWidth"></div>
        </td>
      </tr>
    </tbody>
  </table>
</div>

<!-- CARD MODE -->
<div *ngIf="type === 'card'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
  <div *ngFor="let _ of range" class="border rounded bg-white p-4 shadow-sm">
    <div class="flex justify-between items-start mb-3">
      <div class="w-full">
        <div class="h-5 bg-gray-200 rounded w-1/3 skeleton-shimmer mb-2"></div>
        <div class="h-4 bg-gray-100 rounded w-1/4 skeleton-shimmer"></div>
      </div>
      <div class="h-6 bg-gray-200 rounded w-12 skeleton-shimmer"></div>
    </div>

    <div class="space-y-3 mb-4">
      <div class="h-3 bg-gray-100 rounded w-3/4 skeleton-shimmer"></div>
      <div class="h-3 bg-gray-100 rounded w-1/2 skeleton-shimmer"></div>
      <div class="h-3 bg-gray-100 rounded w-2/3 skeleton-shimmer"></div>
    </div>

    <div class="flex justify-end pt-2 border-t border-gray-100">
      <div class="h-8 w-8 bg-gray-200 rounded-full skeleton-shimmer"></div>
    </div>
  </div>
</div>

<!-- FOLLOWUP CARD MODE (Dashboard upcoming followups) -->
<div *ngIf="type === 'followup-card'" class="space-y-4">
  <div *ngFor="let _ of range" class="flex items-center justify-between p-3 border border-gray-100 rounded-lg bg-white">
    <div class="flex items-center gap-3">
      <!-- Circular avatar skeleton -->
      <div class="w-10 h-10 rounded-full bg-teal-50 skeleton-shimmer shrink-0"></div>
      <div class="space-y-1.5">
        <!-- Patient name skeleton -->
        <div class="h-4 bg-gray-200 rounded skeleton-shimmer w-32"></div>
        <!-- Program name skeleton -->
        <div class="h-3 bg-gray-100 rounded skeleton-shimmer w-24"></div>
        <!-- Date / time pills skeleton -->
        <div class="flex items-center gap-2 pt-0.5">
          <div class="h-3.5 bg-teal-100/60 rounded skeleton-shimmer w-14"></div>
          <div class="h-3 bg-gray-100 rounded skeleton-shimmer w-20"></div>
        </div>
      </div>
    </div>
    <!-- Call button skeleton -->
    <div class="p-1.5 rounded-full">
      <div class="w-7 h-7 rounded-full bg-teal-50 skeleton-shimmer"></div>
    </div>
  </div>
</div>

<!-- DRAWER FOLLOWUP MODE -->
<div *ngIf="type === 'drawer-followup'" class="space-y-3">
  <div *ngFor="let _ of range" class="p-3.5 rounded-xl border border-gray-200/80 bg-white flex flex-col gap-3">
    <!-- Top Row: Patient Info & Timing Status -->
    <div class="flex items-start justify-between gap-3">
      <div class="flex items-center gap-3 min-w-0">
        <!-- Avatar -->
        <div class="w-10 h-10 rounded-full bg-teal-50 skeleton-shimmer shrink-0"></div>
        <!-- Name & Program -->
        <div class="min-w-0 space-y-1.5">
          <div class="flex items-center gap-2">
            <div class="h-4 bg-gray-200 rounded skeleton-shimmer w-28"></div>
            <div class="h-4 bg-slate-100 rounded skeleton-shimmer w-16"></div>
          </div>
          <div class="h-3 bg-gray-100 rounded skeleton-shimmer w-32"></div>
        </div>
      </div>
      <!-- Timing Badge -->
      <div class="h-5 w-16 rounded-full bg-gray-100 skeleton-shimmer shrink-0"></div>
    </div>

    <!-- Bottom Row: Date/Time & Action Buttons -->
    <div class="flex items-center justify-between pt-2 border-t border-gray-100">
      <div class="h-3.5 bg-gray-100 rounded skeleton-shimmer w-36"></div>
      <div class="flex items-center gap-2">
        <div class="h-6 w-20 rounded-md bg-gray-100 skeleton-shimmer"></div>
        <div class="h-6 w-14 rounded-md bg-teal-100/70 skeleton-shimmer"></div>
      </div>
    </div>
  </div>
</div>

<!-- TEXT MODE -->
<div *ngIf="type === 'text'" class="flex flex-col gap-2 w-full">
  <div *ngFor="let _ of range" class="h-4 bg-gray-200 rounded skeleton-shimmer w-full"></div>
</div>

<!-- STATS MODE (same card shell as app-stats-card) -->
<div
  *ngIf="type === 'stats'"
  class="grid grid-cols-2 gap-3"
  [ngClass]="{
    'lg:grid-cols-2': statsCount === 2,
    'md:grid-cols-2 lg:grid-cols-3': statsCount === 3,
    'md:grid-cols-2 lg:grid-cols-4': statsCount === 4,
    'md:grid-cols-3 lg:grid-cols-5': statsCount === 5,
    'md:grid-cols-3 lg:grid-cols-6': statsCount === 6
  }"
>
  <div *ngFor="let _ of statsRange" class="med-surface rounded-xl px-5 py-4 h-full flex flex-col justify-between">
    <div class="flex items-center justify-between gap-2" [class.mb-4]="statsType === 'detailed'">
      <div class="h-[10px] w-24 rounded skeleton-shimmer"></div>
      <div class="w-7 h-7 rounded-lg skeleton-shimmer flex-shrink-0"></div>
    </div>

    <!-- Detailed Mode: List of items -->
    <div *ngIf="statsType === 'detailed'" class="mt-auto space-y-1.5">
      <div *ngFor="let i of [1,2,3]" class="flex justify-between items-center px-2.5 py-1.5 bg-slate-50/60 border border-slate-100/40 rounded-lg">
        <div class="h-3 bg-gray-200 rounded skeleton-shimmer w-16"></div>
        <div class="h-3 bg-gray-200 rounded skeleton-shimmer w-12"></div>
      </div>
    </div>

    <!-- Simple Mode: Single big value -->
    <div *ngIf="statsType === 'simple'" class="flex items-baseline gap-2 mt-auto">
      <div class="h-8 w-16 rounded skeleton-shimmer"></div>
    </div>
  </div>
</div>

<!-- DASHBOARD MODE -->
<div *ngIf="type === 'dashboard'" class="w-full">
  <!-- Stats Cards -->
  <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
    <div *ngFor="let _ of [1,2,3,4]" class="bg-white p-5 rounded border">
      <div class="h-8 bg-gray-200 rounded skeleton-shimmer w-16 mb-2"></div>
      <div class="h-4 bg-gray-100 rounded skeleton-shimmer w-24"></div>
    </div>
  </div>

  <!-- Charts Row 1 -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
    <div *ngFor="let _ of [1,2]" class="bg-white p-4 rounded border h-[350px]">
      <div class="h-6 bg-gray-200 rounded skeleton-shimmer w-40 mb-4"></div>
      <div class="h-[280px] bg-gray-100 rounded skeleton-shimmer w-full"></div>
    </div>
  </div>

  <!-- Charts Row 2 -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
    <div *ngFor="let _ of [1,2]" class="bg-white p-4 rounded border h-[350px]">
       <div class="h-6 bg-gray-200 rounded skeleton-shimmer w-32 mb-4"></div>
       <div class="h-[280px] bg-gray-100 rounded skeleton-shimmer w-full"></div>
    </div>
  </div>

  <!-- Bottom Section -->
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
    <div class="bg-white p-4 rounded-xl border border-gray-100 shadow-sm h-auto">
      <div class="flex justify-between mb-4">
        <div class="h-6 bg-gray-200 rounded skeleton-shimmer w-40"></div>
        <div class="h-6 bg-gray-100 rounded skeleton-shimmer w-24"></div>
      </div>
      <div class="space-y-4">
        <div *ngFor="let _ of [1,2,3]" class="flex items-center justify-between p-3 border border-gray-100 rounded-lg">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-teal-50 skeleton-shimmer shrink-0"></div>
            <div class="space-y-1.5">
              <div class="h-4 bg-gray-200 rounded skeleton-shimmer w-28"></div>
              <div class="h-3 bg-gray-100 rounded skeleton-shimmer w-36"></div>
              <div class="flex items-center gap-2 pt-0.5">
                <div class="h-3.5 bg-teal-100/60 rounded skeleton-shimmer w-14"></div>
                <div class="h-3 bg-gray-100 rounded skeleton-shimmer w-20"></div>
              </div>
            </div>
          </div>
          <div class="w-7 h-7 rounded-full bg-teal-50 skeleton-shimmer shrink-0"></div>
        </div>
      </div>
    </div>

    <div class="bg-white p-4 rounded border h-64">
      <div class="flex justify-between mb-4">
         <div class="h-6 bg-gray-200 rounded skeleton-shimmer w-40"></div>
         <div class="h-6 bg-gray-100 rounded skeleton-shimmer w-24"></div>
      </div>
      <div class="grid grid-cols-2 gap-4 mb-4">
        <div class="h-16 bg-gray-50 rounded skeleton-shimmer"></div>
        <div class="h-16 bg-gray-50 rounded skeleton-shimmer"></div>
      </div>
      <div class="flex gap-3">
        <div class="h-10 bg-gray-200 rounded skeleton-shimmer w-32"></div>
        <div class="h-10 bg-gray-200 rounded skeleton-shimmer w-32"></div>
      </div>
    </div>
  </div>
</div>

<!-- LIST PAGE MODE (stats → filters → action bar → table → pagination) -->
<div *ngIf="type === 'list-page'" aria-busy="true" aria-label="Loading">
  <!-- Stats cards -->
  <div *ngIf="statsLabels.length" class="grid grid-cols-2 gap-3 mb-6"
    [ngClass]="{ 'sm:grid-cols-3': statsLabels.length === 3, 'sm:grid-cols-4': statsLabels.length === 4, 'sm:grid-cols-5': statsLabels.length === 5, 'sm:grid-cols-6': statsLabels.length >= 6 }">
    <div *ngFor="let label of statsLabels" class="med-surface flex flex-col justify-between rounded-xl px-5 py-4">
      <div class="flex items-center justify-between gap-2">
        <h3 class="truncate text-[10px] leading-[14px] font-semibold uppercase tracking-[0.05em] text-[#8FADB2]">{{ label }}</h3>
        <div class="w-7 h-7 rounded-lg skeleton-shimmer flex-shrink-0"></div>
      </div>
      <div class="flex items-baseline gap-2 mt-auto">
        <div class="h-8 w-16 rounded skeleton-shimmer"></div>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div *ngIf="filterLabels.length" class="mb-4">
    <div class="bg-white p-5 border border-[#DFE8E9] rounded-xl shadow-[0_1px_3px_rgba(24,60,64,0.04)] mb-4 w-full">
      <div class="grid gap-4 w-full" [ngClass]="filtersGridClass">
        <div *ngFor="let label of filterLabels">
          <label class="block text-[10px] font-semibold text-[#62777E] uppercase tracking-wider mb-1.5">{{ label }}</label>
          <div class="h-[44px] w-full rounded-xl skeleton-shimmer"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Standalone selects -->
  <div *ngIf="inlineFilterWidths.length" class="flex flex-wrap gap-3 mb-4">
    <div *ngFor="let w of inlineFilterWidths" [class]="'h-[38px] rounded-lg skeleton-shimmer ' + w"></div>
  </div>

  <!-- Action bar -->
  <div *ngIf="showSearch || actionWidths.length" class="flex items-center justify-between gap-3 mb-4 flex-wrap w-full">
    <div class="flex items-center gap-3 flex-wrap">
      <div *ngIf="showSearch" class="h-8 w-64 rounded-lg skeleton-shimmer"></div>
    </div>
    <div class="flex items-center gap-2 flex-wrap">
      <div *ngFor="let w of actionWidths" [class]="'h-8 rounded-lg skeleton-shimmer ' + w"></div>
    </div>
  </div>

  <!-- Table -->
  <div class="table-container">
    <table class="w-full text-left border-collapse text-sm" [style.min-width.px]="tableMinWidth">
      <thead class="border-b border-gray-200">
        <tr>
          <th *ngFor="let col of columns" class="table-header" [ngClass]="col.headerClass || ''" [class.col-actions]="col.cell === 'actions'">{{ col.label }}</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let _ of range" class="group">
          <td *ngFor="let col of columns" [class.col-actions]="col.cell === 'actions'" [class.table-item]="col.cell === 'two-line'">
            <div [ngClass]="col.align === 'center' ? 'flex justify-center' : ''">
            <ng-container [ngSwitch]="col.cell">
              <div *ngSwitchCase="'actions'" class="p-1.5 flex items-center justify-center">
                <div class="h-4 w-4 rounded skeleton-shimmer"></div>
              </div>
              <ng-container *ngSwitchCase="'two-line'">
                <div [class]="'h-[13px] rounded skeleton-shimmer ' + (col.width || 'w-28')"></div>
                <div class="h-[11px] w-16 rounded skeleton-shimmer mt-1"></div>
              </ng-container>
              <div *ngSwitchCase="'badge'" [class]="'h-5 rounded-full skeleton-shimmer ' + (col.width || 'w-20')"></div>
              <div *ngSwitchDefault [class]="'h-[13px] rounded skeleton-shimmer ' + (col.width || 'w-24')"></div>
            </ng-container>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <div *ngIf="showPagination" class="mt-4 max-md:hidden">
    <div class="flex flex-col md:flex-row justify-between items-center gap-4 mt-4">
      <div class="h-4 w-44 rounded skeleton-shimmer"></div>
      <div class="flex items-center gap-1.5">
        <div *ngFor="let _ of [1, 2, 3, 4, 5]" class="h-8 w-[38px] rounded-lg skeleton-shimmer"></div>
      </div>
    </div>
  </div>
</div>

<!-- CHART MODE (placeholder for a chart canvas) -->
<div *ngIf="type === 'chart'" class="w-full rounded-lg skeleton-shimmer" [style.height.px]="height" aria-busy="true"></div>

<!-- STAT CARD MODE (bare cards for use inside an existing grid, same shell as app-stats-card) -->
<ng-container *ngIf="type === 'stat-card'">
  <div *ngFor="let label of statCardItems" class="med-surface rounded-xl px-5 py-4 flex flex-col justify-between">
    <div class="flex items-center justify-between gap-2">
      <h3 *ngIf="label" class="truncate text-[10px] leading-[14px] font-semibold uppercase tracking-[0.05em] text-[#8FADB2]">{{ label }}</h3>
      <div *ngIf="!label" class="h-[10px] w-24 rounded skeleton-shimmer"></div>
      <div class="w-7 h-7 rounded-lg skeleton-shimmer flex-shrink-0"></div>
    </div>
    <div class="h-8 w-16 rounded skeleton-shimmer mt-auto"></div>
  </div>
</ng-container>
